import Phaser from 'phaser';
import { MazeGrid } from '../game/MazeGrid';
import { Player } from '../game/Player';
import { Remnant } from '../game/Remnant';
import { GameState } from '../game/GameState';
import { CooldownTimer } from '../game/CooldownTimer';
import { SoundManager } from '../game/SoundManager';
import { Wall, GridCell, Phase } from '../types';
import { findPath } from '../utils/pathfinder';
import {
  GRID_WIDTH, GRID_HEIGHT, CELL_SIZE,
  COLORS, WALL_THICKNESS,
  CANVAS_WIDTH, CANVAS_HEIGHT, REMNANT_COUNT,
  LEFT_PANEL_WIDTH, TOP_PANEL_HEIGHT, MAZE_WIDTH, MAZE_HEIGHT, VISION_RADIUS,
  SCORE_PER_SECOND, SCORE_EXIT_BONUS, MAX_LEVEL, getLevelConfig,
} from '../utils/constants';

/** High scores — persisted to localStorage */
const STORAGE_KEY = 'labyrinth_high_scores';

function loadHighScores(): number[] {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (data) {
      const parsed = JSON.parse(data);
      if (Array.isArray(parsed)) return parsed.slice(0, 5);
    }
  } catch { /* ignore */ }
  return [];
}

function saveHighScores(scores: number[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(scores));
  } catch { /* ignore */ }
}

const highScores: number[] = loadHighScores();

export class GameScene extends Phaser.Scene {
  private maze!: MazeGrid;
  private player!: Player;
  private gameState!: GameState;
  private cooldownTimer!: CooldownTimer;
  private aiCooldownTimer!: CooldownTimer;
  private mazeGraphics!: Phaser.GameObjects.Graphics;
  private exitRect!: Phaser.GameObjects.Rectangle;
  private remnants: Remnant[] = [];
  private overlayText!: Phaser.GameObjects.Text;
  private restartKey!: Phaser.Input.Keyboard.Key;
  private continueKey!: Phaser.Input.Keyboard.Key;
  private carryOverScore = 0;
  private hoverGraphics!: Phaser.GameObjects.Graphics;
  private fogGraphics!: Phaser.GameObjects.Graphics;
  private soundManager: SoundManager = new SoundManager();
  private soundStarted = false;
  private demoMode = false;
  private demoWallCache: { row: number; col: number; wf: number; adding: boolean } | null = null;
  private demoWallCacheFrame = -999;
  private aiWallCache: { row: number; col: number; wallFlag: number; isAdding: boolean } | null = null;
  private aiWallCacheFrame = -999;
  private frameCount = 0;
  private score = 0;
  private gameStartTime = 0;
  private level = 1;
  private lives = 3;
  private recentPlayerToggles: Map<string, number> = new Map(); // key -> timestamp
  private hoveredEdge: { row: number; col: number; wallFlag: number } | null = null;

  constructor() {
    super({ key: 'GameScene' });
  }

  init(data?: { demo?: boolean; carryOverScore?: number; level?: number; lives?: number }): void {
    this.demoMode = data?.demo ?? false;
    this.carryOverScore = data?.carryOverScore ?? 0;
    this.level = data?.level ?? 1;
    this.lives = data?.lives ?? 3;
  }

  create(): void {
    this.gameState = new GameState();
    this.gameState.startGame();
    const cfg = getLevelConfig(this.level);
    this.cooldownTimer = new CooldownTimer(cfg.playerCooldown);
    this.aiCooldownTimer = new CooldownTimer(cfg.aiCooldown);
    this.score = 0;
    this.gameStartTime = 0;

    // Generate maze
    this.maze = new MazeGrid(GRID_WIDTH, GRID_HEIGHT);

    // Draw maze
    this.mazeGraphics = this.add.graphics();
    this.drawMaze();

    // Draw exit
    const exitPos = this.maze.cellToPixel(this.maze.exitCell.row, this.maze.exitCell.col);
    this.exitRect = this.add.rectangle(
      exitPos.x, exitPos.y,
      CELL_SIZE * 0.8, CELL_SIZE * 0.8,
      COLORS.EXIT, 0.5,
    );
    this.exitRect.setDepth(1);

    // Create player
    this.player = new Player(this, this.maze, this.soundManager);
    this.player.autoMode = this.demoMode;

    // Create Remnants at distributed positions
    this.remnants = [];
    const spawnPositions = this.computeRemnantSpawns();
    for (let i = 0; i < REMNANT_COUNT; i++) {
      const sp = spawnPositions[i];
      this.remnants.push(new Remnant(this, this.maze, i, sp.row, sp.col, cfg.remnantTweenDuration));
    }

    // Overlay text (hidden initially)
    this.overlayText = this.add.text(
      LEFT_PANEL_WIDTH + MAZE_WIDTH / 2, TOP_PANEL_HEIGHT + MAZE_HEIGHT / 2, '',
      {
        fontSize: '36px',
        color: '#ffffff',
        fontStyle: 'bold',
        align: 'center',
        backgroundColor: 'rgba(0,0,0,0.7)',
        padding: { x: 24, y: 16 },
      },
    ).setOrigin(0.5).setDepth(100).setVisible(false);

    // Restart key
    if (this.input.keyboard) {
      this.restartKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.R);
      this.continueKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.C);
    }

    // Wall click interaction
    this.hoverGraphics = this.add.graphics();
    this.hoverGraphics.setDepth(5);

    // Fog of war overlay (drawn above maze but below overlay text)
    this.fogGraphics = this.add.graphics();
    this.fogGraphics.setDepth(20);

    // Invisible interactive zone covering the maze — captures all clicks
    // at highest depth so nothing blocks it
    const clickZone = this.add.rectangle(
      LEFT_PANEL_WIDTH + MAZE_WIDTH / 2, TOP_PANEL_HEIGHT + MAZE_HEIGHT / 2,
      MAZE_WIDTH, MAZE_HEIGHT,
      0x000000, 0,
    );
    clickZone.setDepth(50);
    clickZone.setInteractive();
    this.setupWallClickHandler(clickZone);

    // Launch UI scene for cooldown indicators + score
    this.scene.launch('UIScene', {
      playerCooldown: this.cooldownTimer,
      aiCooldown: this.aiCooldownTimer,
      getScore: () => this.score,
      highScores: highScores,
      demoMode: this.demoMode,
      level: this.level,
      getLives: () => this.lives,
    });
  }

  update(): void {
    if (this.gameState.isGameOver) {
      this.soundManager.updateWraithProximity(Infinity);
      if (this.demoMode) return; // auto-restart handled by delayed call

      // C to continue (victory → next level, defeat with lives → retry level)
      if (this.continueKey && Phaser.Input.Keyboard.JustDown(this.continueKey)) {
        if (this.gameState.phase === Phase.VICTORY) {
          const nextLevel = Math.min(this.level + 1, MAX_LEVEL);
          this.soundManager.stop();
          this.soundStarted = false;
          this.scene.stop('UIScene');
          this.scene.restart({ carryOverScore: this.score, level: nextLevel, lives: this.lives });
          return;
        }
        if (this.gameState.phase === Phase.DEFEAT && this.lives > 0) {
          this.soundManager.stop();
          this.soundStarted = false;
          this.scene.stop('UIScene');
          this.scene.restart({ carryOverScore: this.score, level: this.level, lives: this.lives });
          return;
        }
      }

      // R to restart (always resets to level 1, score 0, 3 lives)
      if (this.restartKey && Phaser.Input.Keyboard.JustDown(this.restartKey)) {
        this.soundManager.stop();
        this.soundStarted = false;
        this.scene.stop('UIScene');
        this.scene.restart({ demo: false, carryOverScore: 0, level: 1, lives: 3 });
      }
      return;
    }

    if (!this.gameState.isPlaying) return;
    this.frameCount++;

    // Start audio (requires prior user interaction for non-demo)
    if (!this.soundStarted && !this.demoMode) {
      this.soundManager.start();
      this.soundStarted = true;
    }

    // Update score (time-based + carry-over from previous rounds)
    if (this.gameStartTime === 0) this.gameStartTime = this.time.now;
    if (!this.demoMode) {
      this.score = this.carryOverScore + Math.floor((this.time.now - this.gameStartTime) / 1000 * SCORE_PER_SECOND);
    }

    this.player.update();

    // Update Remnants
    for (const remnant of this.remnants) {
      remnant.update(this.player.gridRow, this.player.gridCol);
    }

    // Redraw cooldown hover indicator so the clock icon animates
    if (this.hoveredEdge && !this.cooldownTimer.isReady) {
      this.hoverGraphics.clear();
      const { row, col, wallFlag } = this.hoveredEdge;
      this.drawWallHighlight(row, col, wallFlag, COLORS.WALL_HOVER_DISABLED);
      this.drawCooldownIcon(row, col, wallFlag);
    } else if (this.hoveredEdge && this.cooldownTimer.isReady) {
      // Cooldown just finished — redraw as active color, then stop redrawing
      this.hoverGraphics.clear();
      const { row, col, wallFlag } = this.hoveredEdge;
      const hasWall = this.maze.wallExists(row, col, wallFlag);
      const color = hasWall ? COLORS.WALL_HOVER_REMOVE : COLORS.WALL_HOVER_ADD;
      this.drawWallHighlight(row, col, wallFlag, color);
    }

    // AI wall toggle (Remnant AI)
    this.updateAIWall();

    // Demo mode: auto-toggle walls on behalf of the player
    if (this.demoMode) {
      this.updateDemoPlayerWall();
    }

    // Update wraith proximity sound
    let closestDist = Infinity;
    for (const remnant of this.remnants) {
      const dist = Math.abs(remnant.gridRow - this.player.gridRow)
        + Math.abs(remnant.gridCol - this.player.gridCol);
      if (dist < closestDist) closestDist = dist;
    }
    this.soundManager.updateWraithProximity(closestDist);

    // Update fog of war and Remnant visibility (skip in demo)
    if (!this.demoMode) {
      this.updateFog();
    }

    // Check Remnant collision (grid-based + pixel proximity)
    for (const remnant of this.remnants) {
      const sameCell = remnant.gridRow === this.player.gridRow
        && remnant.gridCol === this.player.gridCol;
      const dx = remnant.sprite.x - this.player.sprite.x;
      const dy = remnant.sprite.y - this.player.sprite.y;
      const tooClose = Math.sqrt(dx * dx + dy * dy) < CELL_SIZE * 0.4;

      if (sameCell || tooClose) {
        this.gameState.defeat();
        if (!this.demoMode) {
          this.lives--;
          this.saveHighScore();
          this.soundManager.playScream();
        }
        this.playDeathSequence(remnant);
        if (this.demoMode) {
          this.time.delayedCall(3500, () => {
            this.soundManager.stop();
            this.soundStarted = false;
            this.scene.stop('UIScene');
            this.scene.restart({ demo: true });
          });
        }
        return;
      }
    }

    // Check exit
    if (
      this.player.gridRow === this.maze.exitCell.row &&
      this.player.gridCol === this.maze.exitCell.col
    ) {
      this.gameState.victory();
      if (!this.demoMode) {
        this.score += SCORE_EXIT_BONUS;
        this.saveHighScore();
      }
      // Freeze all Remnants
      for (const r of this.remnants) {
        r.isMoving = true;
      }
      if (this.demoMode) {
        this.time.delayedCall(1000, () => {
          this.soundManager.stop();
          this.soundStarted = false;
          this.scene.stop('UIScene');
          this.scene.restart({ demo: true });
        });
      } else {
        this.playVictorySequence();
      }
    }
  }

  drawMaze(): void {
    this.mazeGraphics.clear();

    // Background
    this.mazeGraphics.fillStyle(COLORS.BACKGROUND, 1);
    this.mazeGraphics.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    // Cell floors
    this.mazeGraphics.fillStyle(0x16213e, 1);
    for (let row = 0; row < this.maze.height; row++) {
      for (let col = 0; col < this.maze.width; col++) {
        this.mazeGraphics.fillRect(
          LEFT_PANEL_WIDTH + col * CELL_SIZE + 1,
          TOP_PANEL_HEIGHT + row * CELL_SIZE + 1,
          CELL_SIZE - 2,
          CELL_SIZE - 2,
        );
      }
    }

    // Outer wall frame — inset so stroke stays within the maze area
    const hw = WALL_THICKNESS / 2;
    this.mazeGraphics.lineStyle(WALL_THICKNESS, COLORS.WALL, 1);
    this.mazeGraphics.strokeRect(
      LEFT_PANEL_WIDTH + hw, TOP_PANEL_HEIGHT + hw,
      MAZE_WIDTH - WALL_THICKNESS, MAZE_HEIGHT - WALL_THICKNESS,
    );

    // Walls (skip outer boundary)
    this.mazeGraphics.lineStyle(WALL_THICKNESS, COLORS.WALL, 1);
    for (let row = 0; row < this.maze.height; row++) {
      for (let col = 0; col < this.maze.width; col++) {
        const x = LEFT_PANEL_WIDTH + col * CELL_SIZE;
        const y = TOP_PANEL_HEIGHT + row * CELL_SIZE;
        const cell = this.maze.cells[row][col];

        if ((cell & Wall.NORTH) && row > 0) {
          this.mazeGraphics.strokeLineShape(
            new Phaser.Geom.Line(x, y, x + CELL_SIZE, y),
          );
        }
        if ((cell & Wall.EAST) && col < this.maze.width - 1) {
          this.mazeGraphics.strokeLineShape(
            new Phaser.Geom.Line(x + CELL_SIZE, y, x + CELL_SIZE, y + CELL_SIZE),
          );
        }
        if ((cell & Wall.SOUTH) && row < this.maze.height - 1) {
          this.mazeGraphics.strokeLineShape(
            new Phaser.Geom.Line(x, y + CELL_SIZE, x + CELL_SIZE, y + CELL_SIZE),
          );
        }
        if ((cell & Wall.WEST) && col > 0) {
          this.mazeGraphics.strokeLineShape(
            new Phaser.Geom.Line(x, y, x, y + CELL_SIZE),
          );
        }
      }
    }
  }

  showOverlay(text: string): void {
    this.overlayText.setText(text);
    this.overlayText.setVisible(true);
  }

  getMaze(): MazeGrid {
    return this.maze;
  }

  getPlayer(): Player {
    return this.player;
  }

  getGameState(): GameState {
    return this.gameState;
  }

  getCooldownTimer(): CooldownTimer {
    return this.cooldownTimer;
  }

  getRemnants(): Remnant[] {
    return this.remnants;
  }

  getMazeGraphics(): Phaser.GameObjects.Graphics {
    return this.mazeGraphics;
  }

  private setupWallClickHandler(clickZone: Phaser.GameObjects.Rectangle): void {
    clickZone.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (!this.gameState.isPlaying) return;
      if (!this.cooldownTimer.isReady) return;

      const edge = this.getNearestWallEdge(pointer.x, pointer.y);
      if (!edge) return;

      const { row, col, wallFlag } = edge;

      // Reject outer boundary
      if (this.maze.isOuterWall(row, col, wallFlag)) return;

      const isAdding = !this.maze.wallExists(row, col, wallFlag);

      // Toggle the wall
      if (this.maze.toggleWall(row, col, wallFlag)) {
        if (isAdding) {
          this.soundManager.playWallBuild();
        } else {
          this.soundManager.playWallDemolish();
        }

        // Record this toggle so AI can't immediately undo it
        this.recentPlayerToggles.set(`${row},${col},${wallFlag}`, Date.now());

        this.cooldownTimer.trigger();
        this.drawMaze();

        // Force all Remnants to recalculate
        for (const remnant of this.remnants) {
          remnant.invalidatePath();
        }
      }
    });

    // Hover highlight — use scene-level so it works even outside the zone
    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      this.hoverGraphics.clear();
      if (!this.gameState.isPlaying) { this.hoveredEdge = null; return; }

      const edge = this.getNearestWallEdge(pointer.x, pointer.y);
      if (!edge || this.maze.isOuterWall(edge.row, edge.col, edge.wallFlag)) {
        this.hoveredEdge = null;
        return;
      }

      this.hoveredEdge = edge;

      if (this.cooldownTimer.isReady) {
        const hasWall = this.maze.wallExists(edge.row, edge.col, edge.wallFlag);
        const color = hasWall ? COLORS.WALL_HOVER_REMOVE : COLORS.WALL_HOVER_ADD;
        this.drawWallHighlight(edge.row, edge.col, edge.wallFlag, color);
      } else {
        this.drawWallHighlight(edge.row, edge.col, edge.wallFlag, COLORS.WALL_HOVER_DISABLED);
        this.drawCooldownIcon(edge.row, edge.col, edge.wallFlag);
      }
    });
  }

  private getNearestWallEdge(
    px: number, py: number,
  ): { row: number; col: number; wallFlag: number } | null {
    const mx = px - LEFT_PANEL_WIDTH; // adjust for panel offset
    const my = py - TOP_PANEL_HEIGHT; // adjust for top panel
    if (mx < 0 || my < 0) return null;

    // Work in continuous grid coordinates (not snapped to a cell)
    const gx = mx / CELL_SIZE;
    const gy = my / CELL_SIZE;

    // Find the nearest horizontal edge (between rows) and vertical edge (between cols)
    const nearestHRow = Math.round(gy); // nearest horizontal grid line
    const nearestVCol = Math.round(gx); // nearest vertical grid line
    const distH = Math.abs(gy - nearestHRow);
    const distV = Math.abs(gx - nearestVCol);

    const threshold = 0.35; // Generous click zone (35% of cell from edge)

    if (distH > threshold && distV > threshold) return null;

    if (distH <= distV) {
      // Horizontal edge — wall between row above and row below
      const cellRow = nearestHRow; // cell whose NORTH edge this is
      const cellCol = Math.floor(gx);
      // Could be NORTH of cellRow or SOUTH of (cellRow-1) — normalize
      if (cellCol < 0 || cellCol >= this.maze.width) return null;
      if (nearestHRow > 0 && nearestHRow < this.maze.height) {
        return { row: nearestHRow, col: cellCol, wallFlag: Wall.NORTH };
      } else if (nearestHRow === 0) {
        return { row: 0, col: cellCol, wallFlag: Wall.NORTH };
      } else if (nearestHRow === this.maze.height) {
        return { row: this.maze.height - 1, col: cellCol, wallFlag: Wall.SOUTH };
      }
    } else {
      // Vertical edge — wall between col left and col right
      const cellRow = Math.floor(gy);
      const cellCol = nearestVCol;
      if (cellRow < 0 || cellRow >= this.maze.height) return null;
      if (nearestVCol > 0 && nearestVCol < this.maze.width) {
        return { row: cellRow, col: nearestVCol, wallFlag: Wall.WEST };
      } else if (nearestVCol === 0) {
        return { row: cellRow, col: 0, wallFlag: Wall.WEST };
      } else if (nearestVCol === this.maze.width) {
        return { row: cellRow, col: this.maze.width - 1, wallFlag: Wall.EAST };
      }
    }

    return null;
  }

  private drawWallHighlight(row: number, col: number, wallFlag: number, color: number): void {
    const x = LEFT_PANEL_WIDTH + col * CELL_SIZE;
    const y = TOP_PANEL_HEIGHT + row * CELL_SIZE;
    this.hoverGraphics.lineStyle(WALL_THICKNESS + 2, color, 0.8);

    switch (wallFlag) {
      case Wall.NORTH:
        this.hoverGraphics.strokeLineShape(new Phaser.Geom.Line(x, y, x + CELL_SIZE, y));
        break;
      case Wall.SOUTH:
        this.hoverGraphics.strokeLineShape(new Phaser.Geom.Line(x, y + CELL_SIZE, x + CELL_SIZE, y + CELL_SIZE));
        break;
      case Wall.WEST:
        this.hoverGraphics.strokeLineShape(new Phaser.Geom.Line(x, y, x, y + CELL_SIZE));
        break;
      case Wall.EAST:
        this.hoverGraphics.strokeLineShape(new Phaser.Geom.Line(x + CELL_SIZE, y, x + CELL_SIZE, y + CELL_SIZE));
        break;
    }
  }

  private drawCooldownIcon(row: number, col: number, wallFlag: number): void {
    const x = LEFT_PANEL_WIDTH + col * CELL_SIZE;
    const y = TOP_PANEL_HEIGHT + row * CELL_SIZE;

    // Find midpoint of the wall segment
    let cx: number, cy: number;
    switch (wallFlag) {
      case Wall.NORTH:
        cx = x + CELL_SIZE / 2; cy = y; break;
      case Wall.SOUTH:
        cx = x + CELL_SIZE / 2; cy = y + CELL_SIZE; break;
      case Wall.WEST:
        cx = x; cy = y + CELL_SIZE / 2; break;
      case Wall.EAST:
      default:
        cx = x + CELL_SIZE; cy = y + CELL_SIZE / 2; break;
    }

    const r = 6;
    const g = this.hoverGraphics;

    // Clock face
    g.fillStyle(0x000000, 0.6);
    g.fillCircle(cx, cy, r);
    g.lineStyle(1.5, 0xffffff, 0.9);
    g.strokeCircle(cx, cy, r);

    // Clock hands — minute hand rotates with cooldown progress
    const progress = this.cooldownTimer.progress;
    const angle = -Math.PI / 2 + progress * Math.PI * 2;
    g.lineStyle(1.5, 0xffffff, 0.9);
    // Minute hand
    g.strokeLineShape(new Phaser.Geom.Line(cx, cy, cx + Math.cos(angle) * (r - 2), cy + Math.sin(angle) * (r - 2)));
    // Hour hand (short, fixed upward)
    g.strokeLineShape(new Phaser.Geom.Line(cx, cy, cx, cy - (r - 3)));
  }

  /** Check if a wall was toggled by the player within the last 2 seconds */
  private isRecentPlayerToggle(row: number, col: number, wallFlag: number): boolean {
    const key = `${row},${col},${wallFlag}`;
    const timestamp = this.recentPlayerToggles.get(key);
    if (!timestamp) return false;
    if (Date.now() - timestamp < 2000) return true;
    this.recentPlayerToggles.delete(key);
    return false;
  }

  private wallFlagToDelta(wallFlag: number): { row: number; col: number } {
    switch (wallFlag) {
      case Wall.NORTH: return { row: -1, col: 0 };
      case Wall.SOUTH: return { row: 1, col: 0 };
      case Wall.EAST: return { row: 0, col: 1 };
      case Wall.WEST: return { row: 0, col: -1 };
      default: return { row: 0, col: 0 };
    }
  }

  private playVictorySequence(): void {
    const exitX = this.player.sprite.x;
    const exitY = this.player.sprite.y;

    this.soundManager.playTeleport();

    // Step 1: Zoom in on the player at the exit
    this.time.delayedCall(100, () => {
      this.cameras.main.pan(exitX, exitY, 500, 'Sine.easeInOut');
      this.cameras.main.zoomTo(3, 500, 'Sine.easeInOut');
    });

    // Step 2: Player spins and shrinks (teleports away)
    this.time.delayedCall(600, () => {
      this.tweens.add({
        targets: this.player.sprite,
        angle: 720,
        scaleX: 0,
        scaleY: 0,
        alpha: 0,
        duration: 800,
        ease: 'Power2',
      });

      // Sparkle particles around the player
      for (let i = 0; i < 16; i++) {
        const angle = (i / 16) * Math.PI * 2;
        const sparkle = this.add.circle(
          exitX, exitY,
          2 + Math.random() * 2,
          0x44ff44,
        );
        sparkle.setDepth(95);
        sparkle.setAlpha(0);

        const dist = 20 + Math.random() * 40;
        this.tweens.add({
          targets: sparkle,
          x: exitX + Math.cos(angle) * dist,
          y: exitY + Math.sin(angle) * dist,
          alpha: { from: 1, to: 0 },
          scaleX: { from: 1.5, to: 0.2 },
          scaleY: { from: 1.5, to: 0.2 },
          duration: 600 + Math.random() * 300,
          delay: Math.random() * 200,
          ease: 'Power2',
          onComplete: () => sparkle.destroy(),
        });
      }
    });

    // Step 3: Zoom back out and show overlay
    this.time.delayedCall(1600, () => {
      this.cameras.main.zoomTo(1, 600, 'Sine.easeOut');
      this.cameras.main.pan(
        CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2, 600, 'Sine.easeOut',
      );
    });

    this.time.delayedCall(2200, () => {
      if (this.level >= MAX_LEVEL) {
        this.showOverlay('YOU ESCAPED!\n\nFinal Score: ' + this.score + '\n\nR to restart');
      } else {
        this.showOverlay('LEVEL ' + this.level + ' COMPLETE!\n\nC to continue\nR to restart');
      }
    });
  }

  private playDeathSequence(killer: Remnant): void {
    // Stop all other Remnants
    for (const r of this.remnants) {
      r.isMoving = true; // freeze them
    }

    const deathX = this.player.sprite.x;
    const deathY = this.player.sprite.y;

    // Step 1: Snap both to same tile, side by side
    const offset = CELL_SIZE * 0.15;
    this.tweens.add({
      targets: this.player.sprite,
      x: deathX - offset,
      y: deathY,
      duration: 150,
    });
    this.tweens.add({
      targets: killer.sprite,
      x: deathX + offset,
      y: deathY,
      duration: 150,
    });

    // Step 2: Zoom camera in (after snap)
    this.time.delayedCall(200, () => {
      this.cameras.main.pan(deathX, deathY, 600, 'Sine.easeInOut');
      this.cameras.main.zoomTo(3, 600, 'Sine.easeInOut');
    });

    // Step 3: Remnant lunges at player (at 800ms)
    this.time.delayedCall(800, () => {
      // Remnant pulses bigger
      this.tweens.add({
        targets: killer.sprite,
        scaleX: 1.8,
        scaleY: 1.8,
        duration: 200,
        ease: 'Back.easeIn',
      });

      // Lunge onto player
      this.tweens.add({
        targets: killer.sprite,
        x: deathX - offset,
        duration: 200,
        ease: 'Power2',
      });
    });

    // Step 4: Player shatters into fragments (at 1050ms)
    this.time.delayedCall(1050, () => {
      // Camera shake
      this.cameras.main.shake(400, 0.015);

      // Hide the player sprite
      this.player.sprite.setVisible(false);

      // Create fragment particles
      const fragCount = 12;
      const fragSize = CELL_SIZE * 0.12;
      for (let i = 0; i < fragCount; i++) {
        const angle = (i / fragCount) * Math.PI * 2 + Math.random() * 0.5;
        const speed = 40 + Math.random() * 60;
        const frag = this.add.rectangle(
          deathX - offset, deathY,
          fragSize + Math.random() * 4,
          fragSize + Math.random() * 4,
          COLORS.PLAYER,
        );
        frag.setDepth(95);
        frag.setAlpha(1);

        const targetX = (deathX - offset) + Math.cos(angle) * speed;
        const targetY = deathY + Math.sin(angle) * speed;

        this.tweens.add({
          targets: frag,
          x: targetX,
          y: targetY,
          alpha: 0,
          scaleX: 0.2,
          scaleY: 0.2,
          angle: Math.random() * 360,
          duration: 600 + Math.random() * 400,
          ease: 'Power2',
          onComplete: () => frag.destroy(),
        });
      }

      // Blood splatter — small red dots
      for (let i = 0; i < 8; i++) {
        const angle = Math.random() * Math.PI * 2;
        const dist = 5 + Math.random() * 25;
        const splat = this.add.circle(
          (deathX - offset) + Math.cos(angle) * dist,
          deathY + Math.sin(angle) * dist,
          2 + Math.random() * 3,
          0xaa0000,
        );
        splat.setDepth(94);
        splat.setAlpha(0);
        this.tweens.add({
          targets: splat,
          alpha: 0.8,
          duration: 150,
          delay: i * 30,
        });
      }
    });

    // Step 5: Red overlay and text (at 1800ms)
    this.time.delayedCall(1800, () => {
      // Zoom back out slowly
      this.cameras.main.zoomTo(1, 800, 'Sine.easeOut');
      this.cameras.main.pan(
        CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2, 800, 'Sine.easeOut',
      );

      // Red overlay clipped to maze area via a geometry mask
      const maskShape = this.make.graphics({ x: 0, y: 0 });
      maskShape.fillRect(LEFT_PANEL_WIDTH, TOP_PANEL_HEIGHT, MAZE_WIDTH, MAZE_HEIGHT);
      const mask = maskShape.createGeometryMask();

      const redOverlay = this.add.rectangle(
        LEFT_PANEL_WIDTH + MAZE_WIDTH / 2, TOP_PANEL_HEIGHT + MAZE_HEIGHT / 2,
        MAZE_WIDTH * 3, MAZE_HEIGHT * 3,
        0x880000, 1,
      );
      redOverlay.setDepth(90);
      redOverlay.setAlpha(0);
      redOverlay.setMask(mask);

      this.tweens.add({
        targets: redOverlay,
        alpha: 0.7,
        duration: 600,
        ease: 'Sine.easeIn',
        onComplete: () => {
          if (!this.demoMode) {
            if (this.lives > 0) {
              this.showOverlay('AARRGHH!\n\n' + this.lives + ' lives left\nC to continue');
            } else {
              this.showOverlay('AARRGHH!\n\nGAME OVER\nR to restart');
            }
          }
        },
      });
    });
  }

  private updateFog(): void {
    this.fogGraphics.clear();

    const pRow = this.player.gridRow;
    const pCol = this.player.gridCol;

    // Torchlight flicker — varies vision radius slightly over time
    const t = this.time.now * 0.001;
    const flicker = Math.sin(t * 3.7) * 0.3
                  + Math.sin(t * 7.1) * 0.15
                  + Math.sin(t * 13.3) * 0.1;
    const effectiveRadius = VISION_RADIUS + flicker;

    for (let row = 0; row < this.maze.height; row++) {
      for (let col = 0; col < this.maze.width; col++) {
        const dist = Math.abs(row - pRow) + Math.abs(col - pCol);

        // Per-cell noise so tiles flicker independently at the edge
        const cellNoise = Math.sin(row * 17.3 + col * 31.7 + t * 5.3) * 0.25
                        + Math.sin(row * 7.1 + col * 13.9 + t * 9.7) * 0.15;
        const cellRadius = effectiveRadius + cellNoise;

        if (dist <= cellRadius - 0.5) continue; // clearly visible

        const x = LEFT_PANEL_WIDTH + col * CELL_SIZE;
        const y = TOP_PANEL_HEIGHT + row * CELL_SIZE;

        if (dist <= cellRadius + 0.5) {
          // Edge of torchlight — flickering partial fog
          const edgeAlpha = 0.15 + (dist - cellRadius + 0.5) * 0.3;
          this.fogGraphics.fillStyle(0x0a0a18, Math.max(0, Math.min(0.5, edgeAlpha)));
          this.fogGraphics.fillRect(x, y, CELL_SIZE, CELL_SIZE);
        } else {
          // Graduated fog: denser the further away
          const alpha = Math.min(0.85, 0.4 + (dist - VISION_RADIUS) * 0.08);
          this.fogGraphics.fillStyle(0x0a0a18, alpha);
          this.fogGraphics.fillRect(x, y, CELL_SIZE, CELL_SIZE);
        }
      }
    }

    // Show/hide Remnants based on vision radius
    for (const remnant of this.remnants) {
      const dist = Math.abs(remnant.gridRow - pRow) + Math.abs(remnant.gridCol - pCol);
      remnant.sprite.setVisible(dist <= VISION_RADIUS);
    }
  }

  /** AI wall control — finds the best wall to toggle to help Remnants reach the player */
  private updateAIWall(): void {
    if (!this.aiCooldownTimer.isReady) return;

    // Cache evaluation every 90 frames (~1.5s)
    if (this.frameCount - this.aiWallCacheFrame > 90) {
      this.aiWallCache = this.findBestAIWallToggle();
      this.aiWallCacheFrame = this.frameCount;
    }

    const best = this.aiWallCache;
    if (!best) return;
    this.aiWallCache = null;

    this.maze.toggleWall(best.row, best.col, best.wallFlag);
    this.aiCooldownTimer.trigger();

    if (best.isAdding) {
      this.soundManager.playWallBuild();
    } else {
      this.soundManager.playWallDemolish();
    }

    this.drawMaze();

    for (const remnant of this.remnants) {
      remnant.invalidatePath();
    }

    this.flashWall(best.row, best.col, best.wallFlag, best.isAdding);
  }

  /**
   * AI strategy: two modes depending on player proximity to exit.
   * 1. Chase mode: remove walls to shorten Remnant-to-player paths.
   * 2. Block mode: add walls to lengthen the player-to-exit path when
   *    the player is closer to the exit than the nearest Remnant is to the player.
   */
  private findBestAIWallToggle(): {
    row: number; col: number; wallFlag: number; isAdding: boolean;
  } | null {
    const playerCell = { row: this.player.gridRow, col: this.player.gridCol };
    const exitCell = this.maze.exitCell;
    const wallFlags = [Wall.NORTH, Wall.EAST, Wall.SOUTH, Wall.WEST];
    const noPathPenalty = this.maze.width * this.maze.height;

    // Measure player distance to exit
    const playerToExit = findPath(
      this.maze.cells, this.maze.height, this.maze.width,
      playerCell, exitCell,
    );
    const playerExitDist = playerToExit.length === 0 ? noPathPenalty : playerToExit.length;

    // Measure shortest Remnant distance to player
    const remnantToPlayerDists = this.remnants.map(r =>
      findPath(
        this.maze.cells, this.maze.height, this.maze.width,
        { row: r.gridRow, col: r.gridCol }, playerCell,
      ).length,
    );
    const closestRemnantDist = Math.min(
      ...remnantToPlayerDists.map(len => len === 0 ? noPathPenalty : len),
    );

    // Block mode: player is closer to exit than nearest Remnant is to player
    const shouldBlock = playerExitDist < closestRemnantDist && playerExitDist > 1;

    if (shouldBlock) {
      return this.findBestBlockToggle(playerCell, exitCell, playerToExit, wallFlags);
    }
    return this.findBestChaseToggle(playerCell, remnantToPlayerDists, wallFlags, noPathPenalty);
  }

  /** Block mode: add a wall along the player-to-exit path to make it longer */
  private findBestBlockToggle(
    playerCell: GridCell,
    exitCell: GridCell,
    playerToExit: GridCell[],
    wallFlags: number[],
  ): { row: number; col: number; wallFlag: number; isAdding: boolean } | null {
    const noPathPenalty = this.maze.width * this.maze.height;
    const currentExitDist = playerToExit.length === 0 ? noPathPenalty : playerToExit.length;

    // Candidate walls: open edges along and near the player-to-exit path
    const candidateKeys = new Set<string>();
    for (const cell of playerToExit) {
      for (let dr = -2; dr <= 2; dr++) {
        for (let dc = -2; dc <= 2; dc++) {
          const cr = cell.row + dr;
          const cc = cell.col + dc;
          if (cr < 0 || cr >= this.maze.height || cc < 0 || cc >= this.maze.width) continue;
          for (const wf of wallFlags) {
            if (this.maze.isOuterWall(cr, cc, wf)) continue;
            if (this.maze.wallExists(cr, cc, wf)) continue; // want open edges to add
            candidateKeys.add(`${cr},${cc},${wf}`);
          }
        }
      }
    }

    let bestToggle: { row: number; col: number; wallFlag: number; isAdding: boolean } | null = null;
    let bestIncrease = 0;

    for (const key of candidateKeys) {
      const [row, col, wf] = key.split(',').map(Number);
      if (this.isRecentPlayerToggle(row, col, wf)) continue;

      // Temporarily add the wall
      this.maze.toggleWall(row, col, wf);

      const newPath = findPath(
        this.maze.cells, this.maze.height, this.maze.width,
        playerCell, exitCell,
      );
      const newDist = newPath.length === 0 ? noPathPenalty : newPath.length;

      // Also check we don't completely block all Remnants from reaching the player
      let remnantStillHasPath = false;
      for (const r of this.remnants) {
        const rPath = findPath(
          this.maze.cells, this.maze.height, this.maze.width,
          { row: r.gridRow, col: r.gridCol }, playerCell,
        );
        if (rPath.length > 0) { remnantStillHasPath = true; break; }
      }

      this.maze.toggleWall(row, col, wf);

      if (!remnantStillHasPath) continue;

      const increase = newDist - currentExitDist;
      if (increase > bestIncrease) {
        bestIncrease = increase;
        bestToggle = { row, col, wallFlag: wf, isAdding: true };
      }
    }

    return bestToggle;
  }

  /** Chase mode: remove walls to shorten Remnant-to-player paths */
  private findBestChaseToggle(
    playerCell: GridCell,
    currentPathLengths: number[],
    wallFlags: number[],
    noPathPenalty: number,
  ): { row: number; col: number; wallFlag: number; isAdding: boolean } | null {
    const currentCost = currentPathLengths.reduce(
      (sum, len) => sum + (len === 0 ? noPathPenalty : len), 0,
    );

    // If all Remnants already have short paths, skip
    if (currentPathLengths.every(len => len > 0 && len < 6)) return null;

    let bestToggle: { row: number; col: number; wallFlag: number; isAdding: boolean } | null = null;
    let bestImprovement = 0;

    const candidateKeys = new Set<string>();
    for (const r of this.remnants) {
      const radius = 8;
      for (let dr = -radius; dr <= radius; dr++) {
        for (let dc = -radius; dc <= radius; dc++) {
          const cr = r.gridRow + dr;
          const cc = r.gridCol + dc;
          if (cr < 0 || cr >= this.maze.height || cc < 0 || cc >= this.maze.width) continue;
          for (const wf of wallFlags) {
            if (this.maze.isOuterWall(cr, cc, wf)) continue;
            if (!this.maze.wallExists(cr, cc, wf)) continue;
            candidateKeys.add(`${cr},${cc},${wf}`);
          }
        }
      }
    }

    for (const key of candidateKeys) {
      const [row, col, wf] = key.split(',').map(Number);
      if (this.isRecentPlayerToggle(row, col, wf)) continue;

      this.maze.toggleWall(row, col, wf);

      let newCost = 0;
      for (const r of this.remnants) {
        const path = findPath(
          this.maze.cells, this.maze.height, this.maze.width,
          { row: r.gridRow, col: r.gridCol }, playerCell,
        );
        newCost += path.length === 0 ? noPathPenalty : path.length;
      }

      this.maze.toggleWall(row, col, wf);

      const improvement = currentCost - newCost;
      if (improvement > bestImprovement) {
        bestImprovement = improvement;
        bestToggle = { row, col, wallFlag: wf, isAdding: false };
      }
    }

    return bestToggle;
  }

  /** Flash a wall segment green (added) or red (removed) to alert the player */
  private flashWall(row: number, col: number, wallFlag: number, wasAdded: boolean): void {
    const color = wasAdded ? COLORS.WALL_HOVER_ADD : COLORS.WALL_HOVER_REMOVE;
    const flashGfx = this.add.graphics();
    flashGfx.setDepth(25);

    const x = LEFT_PANEL_WIDTH + col * CELL_SIZE;
    const y = TOP_PANEL_HEIGHT + row * CELL_SIZE;
    flashGfx.lineStyle(WALL_THICKNESS + 4, color, 1);

    switch (wallFlag) {
      case Wall.NORTH:
        flashGfx.strokeLineShape(new Phaser.Geom.Line(x, y, x + CELL_SIZE, y));
        break;
      case Wall.SOUTH:
        flashGfx.strokeLineShape(new Phaser.Geom.Line(x, y + CELL_SIZE, x + CELL_SIZE, y + CELL_SIZE));
        break;
      case Wall.WEST:
        flashGfx.strokeLineShape(new Phaser.Geom.Line(x, y, x, y + CELL_SIZE));
        break;
      case Wall.EAST:
        flashGfx.strokeLineShape(new Phaser.Geom.Line(x + CELL_SIZE, y, x + CELL_SIZE, y + CELL_SIZE));
        break;
    }

    // Fade out over 1.5 seconds then destroy
    this.tweens.add({
      targets: flashGfx,
      alpha: 0,
      duration: 1500,
      ease: 'Sine.easeOut',
      onComplete: () => flashGfx.destroy(),
    });
  }

  /**
   * Demo mode: AI controls the player's wall toggles.
   * Evaluates once per second (cached), executes when cooldown is ready.
   */
  private updateDemoPlayerWall(): void {
    if (!this.cooldownTimer.isReady) return;

    // Only re-evaluate every 60 frames (~1 second) to avoid chop
    if (this.frameCount - this.demoWallCacheFrame > 60) {
      this.demoWallCache = this.evaluateDemoWallToggle();
      this.demoWallCacheFrame = this.frameCount;
    }

    if (!this.demoWallCache) return;

    const t = this.demoWallCache;
    // Verify the toggle is still valid (maze may have changed)
    const stillValid = this.maze.wallExists(t.row, t.col, t.wf) !== t.adding;
    if (!stillValid) {
      this.demoWallCache = null;
      return;
    }

    this.maze.toggleWall(t.row, t.col, t.wf);
    if (t.adding) {
      this.soundManager.playWallBuild();
    } else {
      this.soundManager.playWallDemolish();
    }
    this.cooldownTimer.trigger();
    this.drawMaze();
    for (const remnant of this.remnants) {
      remnant.invalidatePath();
    }
    this.flashWall(t.row, t.col, t.wf, t.adding);
    this.demoWallCache = null;
  }

  private evaluateDemoWallToggle(): { row: number; col: number; wf: number; adding: boolean } | null {
    const playerCell = { row: this.player.gridRow, col: this.player.gridCol };
    const wallFlags = [Wall.NORTH, Wall.EAST, Wall.SOUTH, Wall.WEST];
    const noPathPenalty = this.maze.width * this.maze.height;

    const currentExitLen = findPath(
      this.maze.cells, this.maze.height, this.maze.width,
      playerCell, this.maze.exitCell,
    ).length || noPathPenalty;

    let currentMinRemnant = Infinity;
    for (const r of this.remnants) {
      const p = findPath(
        this.maze.cells, this.maze.height, this.maze.width,
        { row: r.gridRow, col: r.gridCol }, playerCell,
      );
      const len = p.length === 0 ? noPathPenalty : p.length;
      if (len < currentMinRemnant) currentMinRemnant = len;
    }

    const dangerWeight = currentMinRemnant < 6 ? 4 : currentMinRemnant < 10 ? 2 : 1;

    let best: { row: number; col: number; wf: number; adding: boolean } | null = null;
    let bestScore = 0;

    // Smaller radius (4) to keep evaluation fast
    const radius = 4;
    for (let dr = -radius; dr <= radius; dr++) {
      for (let dc = -radius; dc <= radius; dc++) {
        const cr = this.player.gridRow + dr;
        const cc = this.player.gridCol + dc;
        if (cr < 0 || cr >= this.maze.height || cc < 0 || cc >= this.maze.width) continue;

        for (const wf of wallFlags) {
          if (this.maze.isOuterWall(cr, cc, wf)) continue;
          const exists = this.maze.wallExists(cr, cc, wf);

          this.maze.toggleWall(cr, cc, wf);

          const newExitPath = findPath(
            this.maze.cells, this.maze.height, this.maze.width,
            playerCell, this.maze.exitCell,
          );

          if (newExitPath.length > 0) {
            let newMin = Infinity;
            for (const rem of this.remnants) {
              const p = findPath(
                this.maze.cells, this.maze.height, this.maze.width,
                { row: rem.gridRow, col: rem.gridCol }, playerCell,
              );
              const len = p.length === 0 ? noPathPenalty : p.length;
              if (len < newMin) newMin = len;
            }

            const score = (newMin - currentMinRemnant) * dangerWeight
              + (currentExitLen - newExitPath.length);

            if (score > bestScore) {
              bestScore = score;
              best = { row: cr, col: cc, wf, adding: !exists };
            }
          }

          this.maze.toggleWall(cr, cc, wf);
        }
      }
    }

    return bestScore >= 1 ? best : null;
  }

  private saveHighScore(): void {
    if (this.score > 0) {
      highScores.push(this.score);
      highScores.sort((a, b) => b - a);
      if (highScores.length > 5) highScores.length = 5;
      saveHighScores(highScores);
    }
  }

  private computeRemnantSpawns(): GridCell[] {
    const h = this.maze.height;
    const w = this.maze.width;
    const start = this.maze.startCell;

    const preferred: GridCell[] = [
      { row: Math.floor(h * 0.15), col: Math.floor(w * 0.2) },
      { row: Math.floor(h * 0.15), col: Math.floor(w * 0.8) },
      { row: Math.floor(h * 0.4), col: Math.floor(w * 0.5) },
    ];

    const result: GridCell[] = [];
    for (const pos of preferred) {
      const path = findPath(
        this.maze.cells, this.maze.height, this.maze.width,
        pos, start,
      );
      if (path.length > 0) {
        result.push(pos);
      } else {
        // Find a reachable cell in the same region
        const fallback = this.findReachableSpawn(start, pos, result);
        if (fallback) result.push(fallback);
      }
    }
    return result;
  }

  /** Find a cell near the preferred position that has a path to the player start */
  private findReachableSpawn(
    playerStart: GridCell,
    preferred: GridCell,
    taken: GridCell[],
  ): GridCell | null {
    // Search outward from preferred position
    for (let radius = 1; radius < Math.max(this.maze.width, this.maze.height); radius++) {
      for (let dr = -radius; dr <= radius; dr++) {
        for (let dc = -radius; dc <= radius; dc++) {
          if (Math.abs(dr) + Math.abs(dc) !== radius) continue;
          const r = preferred.row + dr;
          const c = preferred.col + dc;
          if (r < 0 || r >= this.maze.height || c < 0 || c >= this.maze.width) continue;
          if (r === playerStart.row && c === playerStart.col) continue;
          if (taken.some(t => t.row === r && t.col === c)) continue;

          const path = findPath(
            this.maze.cells, this.maze.height, this.maze.width,
            { row: r, col: c }, playerStart,
          );
          if (path.length > 0) return { row: r, col: c };
        }
      }
    }
    return null;
  }
}
